import { format, parseISO } from 'date-fns';
import type { MetricsDatasetV2Response } from '@data/hooks/api/use-metrics-dataset-v2-api/use-metrics-dataset-v2-api';

/**
 * Builds an x-axis subtitle for each assessment period based on the min/max
 * `end_date` for that assessment period in the dataset. Returns e.g.
 * `"(Mar - May)"` when months differ, or `"(May)"` when they match.
 */
export function buildAssessmentPeriodMonths(
  dataset: MetricsDatasetV2Response | undefined
): Record<string, string> {
  if (!dataset) return {};

  const assessmentPeriodIdx = dataset.fields.findIndex(
    (f) => f.key === 'benchmark'
  );
  const endDateIdx = dataset.fields.findIndex((f) => f.key === 'end_date');
  if (assessmentPeriodIdx < 0 || endDateIdx < 0) return {};

  const endDatesByAssessmentPeriod = new Map<string, string[]>();
  dataset.data.forEach((row) => {
    const assessmentPeriod = row[assessmentPeriodIdx] as string;
    const endDate = row[endDateIdx] as string;
    if (!assessmentPeriod || !endDate) return;
    const list = endDatesByAssessmentPeriod.get(assessmentPeriod) ?? [];
    list.push(endDate);
    endDatesByAssessmentPeriod.set(assessmentPeriod, list);
  });

  const months: Record<string, string> = {};
  endDatesByAssessmentPeriod.forEach((dates, assessmentPeriod) => {
    const sorted = [...dates].sort(
      (a, b) => parseISO(a).getTime() - parseISO(b).getTime()
    );
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (!first || !last) return;
    const minMonth = format(parseISO(first), 'MMM');
    const maxMonth = format(parseISO(last), 'MMM');
    months[assessmentPeriod] =
      minMonth === maxMonth ? `(${minMonth})` : `(${minMonth} - ${maxMonth})`;
  });

  return months;
}
